export const meta = {
  id: '12-questions',
  order: 12,
  titleFi: 'Kysymykset',
  titleEn: 'Questions',
  duolingoUnit: 'duo-13-questions',
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: ['sparos', 'skoros', 'spare', 'skore', 'skorī', 'skorkydoso', 'skoriot', 'syt', 'skorverdon', 'ours:ospiknītsos', 'ours:mīvarritir', 'ours:kyrstītsos', 'ours:ȳdranon', 'ours:dōrurtir', 'ours:inditetir', 'ours:vaoresiarzy', 'ours:jorȳdragon'],
  exampleIds: ['ex-11be124ca5b8', 'ex-391477f15eb5', 'ex-ce886e77a7b1', 'ex-c2e38012ce22', 'got-s3e09-001', 'ex-180e7652a1ae', 'hotd-s1e02-007', 'ex-ccac73de0543', 'ex-d8097b534b12', 'hotd-s3e05-011', 'hotd-s1e04-002', 'ex-7ce946209c89', 'ex-0bc2cd55304f', 'ex-94db5f3e7625', 'ex-627cf897558a'],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Questions',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Demonstratives_1',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Pronouns',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Noun_Cases',
    'https://wiki.languageinvention.com/index.php?title=Urnegon',
    // sagon vs ilagon with a place (the usage notes) and the footnote on Rhaenyra's issa (hotd-s3e05-011)
    'https://wiki.languageinvention.com/index.php?title=Sagon',
    'https://wiki.languageinvention.com/index.php?title=Ilagon',
    'https://wiki.languageinvention.com/index.php?title=Season_3_House_of_the_Dragon_Dialogue',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['questions'],
  showLines: ['got-s3e09-001', 'hotd-s1e02-007', 'hotd-s3e05-011', 'hotd-s1e04-002'],
  phrases: ['skorion-massitas'],
  pronunciation: { focus: 'q', words: ['qȳbranna', 'qȳbor', 'valonqar', 'qogror', 'majaqagon'], lines: ['got-s3e09-001'] },
  letter: {
    fi: 'Kysy kumppaniltasi kolme kysymystä. Hän vastaa omassa kirjeessään.',
    en: 'Ask your partner three questions. They answer in their own letter.',
  },
  scene: 'hotd-s1e02-1',
}

# Kysymykset

Tähän asti olemme esittäneet vain kyllä–ei-kysymyksiä. Tällä oppitunnilla tulevat kysymyssanat: kuka, mikä, kenen, milloin, miten, missä, miksi ja kuinka monta. Tässä suomalaisella on etu, sillä valyrian kysymyssanat toimivat yllättävän samoin kuin omamme.

## Kyllä–ei-kysymys: pelkkä sävelkulku

Kyllä–ei-kysymys on valyriassa täsmälleen samanlainen kuin väitelause. Ainoa ero on kysyvä sävelkulku ja kirjoitettuna kysymysmerkki.

<Example id="ex-11be124ca5b8" />

Tässä suomi ei auta, koska meillä kysymyksen merkitsee liite *-ko/-kö* (*Onko Rhaella sinun tätisi?*). Valyriassa vastaavaa liitettä ei ole, joten kysymyksen kuulee vain äänestä. Kun luet ääneen, anna äänen nousta kysyvästi.

## Kysymyssana alkaa s:llä ja tulee alkuun

Duolingon Tips & Notes -ohjeiden mukaan valyrian kysymyssanat alkavat s:llä, tarkemmin sanottuna joko **sp-** tai **sk-**, ja ne tulevat lauseen alkuun.

- **sp-** kysyy elollisesta: <Hv glossed>sparos</Hv> ’kuka’
- **sk-** kysyy elottomasta: <Hv glossed>skoros</Hv> ’mikä’

Tämä on suomalaiselle tuttua. Suomenkin kysymyssanat muodostavat kaksi toisiaan muistuttavien sanojen ryhmää (*kuka, kenen, kuinka*; *mikä, missä, milloin, miksi, miten*), ja ne tulevat tavallisesti lauseen alkuun. Suomikin erottaa elollisen ja elottoman: *kuka* ja *mikä*. Valinta tehdään elollisuuden eikä kieliopillisen suvun mukaan. Sama elollinen–eloton-jako oli jo osoittavissa pronomineissa (<Hv>bony</Hv> ~ <Hv>kony</Hv>).

<Example id="ex-391477f15eb5" />

Kysymyssanan edellä voi silti olla sidesana tai muu lauseen aloittava sana: Rhaenyra sanoo ensin <Hv glossed l="sepār">sepār</Hv> ’niin sitten’ ja <Hv glossed>vēzo gō</Hv> ’ylipäätään’, vasta sitten <Hv glossed>skoro syt</Hv> ’miksi’:

<Example id="ex-627cf897558a" />

### Kuka ja mikä taipuvat

<Hv l="sparos">Sparos</Hv> ja <Hv l="skoros">skoros</Hv> ovat pronomineja, joilla on täysi taivutus. Ne taipuvat kuin aurinkosuvun -os-loppuiset substantiivit. Tärkein uusi muoto on genetiivi <Hv glossed>sparo</Hv> ’kenen’:

<Compare l="sparos" fi="kene|n" hv="spar|o" label="genetiivi: kenen ~ sparo" />

<Example id="ex-ce886e77a7b1" />

<Paradigm lemma="sparos" cases="nom,acc,gen" numbers="sg" />

<Hv l="skoros">Skoros</Hv> esiintyy sarjoissa usein. Ensin wikin esimerkki, sitten Daenerysin repliikki:

<Example id="ex-c2e38012ce22" />

<Line id="got-s3e09-001" />

Wikin pronominisivun mukaan <Hv glossed>Skoros otāpā?</Hv> ’Mitä mieltä olet?’ on vakiintunut ilmaus.

## Mikä niistä? spare ja skore

Kun ’mikä, kumpi’ on substantiivin edellä, se on adjektiivi. Elollisesta kysytään <Hv>spare</Hv> ja elottomasta <Hv>skore</Hv>. Duolingon ohjeiden mukaan ne mukautuvat substantiiviinsa sijassa, suvussa ja luvussa kuten muutkin adjektiivit. Kun ohjeet kirjoitettiin, näitä sanoja ei vielä ollut Duolingon kurssissa, mutta sarjoissa ne ovat käytössä.

<Example id="ex-180e7652a1ae" />

<Interlinear hv="Spar|e val|e ābr|a urne|s?" gloss="mikä-LUN.ACC mies-ACC nainen-NOM nähdä-PRS.3SG" fi="Minkä miehen nainen näkee?" />

Kysymyssana on lauseen alussa, vaikka se kuuluu objektiin. Objektin tunnistaa akkusatiivista (<Hv>vale</Hv>), ei paikasta. Wikin pronominisivun taulukon mukaan maa- ja vesisukuisen sanan edellä muoto on <Hv l="skore">skorior</Hv>, esimerkiksi <Hv glossed>skorior glaeson</Hv> ’mikä elämä’. Rhaenyra kysyy näin, koska <Hv glossed>drōmon</Hv> ’muna’ on maasukua:

<Line id="hotd-s1e02-007" />

## Kokonaisina opittavat kysymyssanat

Duolingon ohjeet neuvovat opettelemaan seuraavat sanat kokonaisina.

- <Hv glossed>skorī</Hv> ’milloin’
- <Hv glossed>skorkydoso</Hv> ’miten’
- <Hv glossed>skoriot</Hv> ’missä’
- <Hv glossed>skoro syt</Hv> ’miksi’

<Pronounce word="skorkydoso" />

<Pronounce l="skoriot" word="skoriot" />

<Example id="ex-ccac73de0543" />

<Example id="ex-d8097b534b12" />

Rhaenyran repliikissä <Hv l="skoriot">skoriot</Hv> toistuu monta kertaa:

<Line id="hotd-s3e05-011" />

Rhaenyran kysymyksissä verbi on <Hv l="sagon">issa</Hv>, ja repliikin alaviite wikissä viittaa sanakirjan huomautukseen: <Hv>sagon</Hv> paikan yhteydessä herättää epäilyn, onko se, mistä kysytään, enää olemassakaan – ehkä se on loppunut tai kadonnut (<LessonLink n={3}>oppitunti 3</LessonLink>). Tavallisesti ’missä jokin on’ kysytään verbillä <Hv glossed>ilagon</Hv> ’maata, sijaita’, ja sen yksikön 3. persoona on <Hv glossed l="ilagon">ilza</Hv>.

Tavallinen kohteliaisuusfraasi *mitä kuuluu?* on sanakirjassa muodossa <Hv glossed>Skorkydoso glaesā?</Hv>, sanatarkasti ’miten elät?’.

<Note>
**Skoros vai skorion?** Elottomasta kysytään kahdella sanalla. Sanakirjan mukaan <Hv glossed>skoros</Hv> ’mikä’ kysyy konkreettista, laskettavaa asiaa, <Hv glossed>skorion</Hv> ’mikä, mikä paikka’ abstraktia tai ei-laskettavaa asiaa ja paikkaa. Tapahtumasta kysytään siksi <Hv>skorion</Hv>. Melisandren kysymys kannattaa opetella kokonaisena (verbin aikamuoto, <Term id="perfekti">perfekti</Term>, tulee <LessonLink n={22}>oppitunnilla 22</LessonLink>). Eron nosti esiin Damien Halme (Academy of High Valyrian).

<Example id="ex-94db5f3e7625" />
</Note>

### Miksi = minkä takia

<Hv fi="minkä takia, miksi">Skoro syt</Hv> on helppo ymmärtää jo nyt. <Hv glossed>Skoro</Hv> on sanan <Hv l="skoros">skoros</Hv> genetiivi ja <Hv glossed>syt</Hv> <Term id="postpositio">postpositio</Term> ’takia, vuoksi, varten’. Wikin sijasivun mukaan kaikki postpositiot vaativat genetiivin, ja suomessa on täsmälleen sama rakenne: *minkä takia*, *kenen vuoksi*. Sanakirjassa on myös <Hv glossed>sparo syt</Hv> ’kenen takia, kenen vuoksi’.

<Compare glossed fi="kene|n takia" hv="spar|o syt" label="genetiivi + postpositio" />

<Line id="hotd-s1e04-002" />

## Kuinka monta? skorverdon

<Hv glossed>Skorverdon</Hv> ’kuinka monta, kuinka paljon’ on erikoistapaus. Duolingon ohjeiden mukaan sitä seuraa aina substantiivi monikon genetiivissä: ’kuinka monta miestä’ on <Hv glossed>skorverdon valoti</Hv>, sanatarkasti ’mikä määrä miehiä’. Varsinainen lauseenjäsen on <Hv>skorverdon</Hv> itse, ja se on yksikössä. Siksi verbi on yksikön 3. persoonassa, vaikka miehiä olisi monta.

<Pronounce word="skorverdon" />

<Example id="ex-7ce946209c89" />

<Interlinear hv="Skorverd|on val|oti ēdru|s?" gloss="kuinka.paljon-NOM mies-GEN.PL nukkua-PRS.3SG" fi="Kuinka monta miestä nukkuu?" />

Tässä suomi auttaa kahdella tavalla. Meilläkin määrää ilmaisevan sanan jälkeen substantiivi on muussa kuin nominatiivissa (*monta miestä*, partitiivi), ja meilläkin verbi on yksikössä: *Kuinka monta miestä nukkuu?* Ero on vain sijassa: suomessa käytetään partitiivia, valyriassa monikon genetiiviä.

<Compare glossed fi="kuinka monta mies|tä" hv="skorverdon val|oti" label="partitiivi ~ monikon genetiivi" />

Vielä yksi esimerkki, jossa tulee vastaan uusi sanakirjamerkitys. Verbi <Hv glossed>urnegon</Hv> ’nähdä’ tarkoittaa myös ’olla jonkin ikäinen’, joten ikää kysytään näin:

<Example id="ex-0bc2cd55304f" />

Huomaa, että esimerkin <Hv l="jēdar-2">jēdaro</Hv> ei ole monikon genetiivi. Sana on <Term id="kollektiivi">kollektiivi</Term>, joten sen genetiivikin on kollektiivissa.

<Practice hv="Sparos ēdrus?" fi="Kuka nukkuu?" />

<Practice hv="Sparo anne ēdrus?" fi="Kenen hevonen nukkuu?" />

<Practice hv="Skorverdon annoti ēdrus?" fi="Kuinka monta hevosta nukkuu?" />

<Practice hv="Skoriot muña ilza?" fi="Missä äiti on?" />

## Lopuksi

Kyllä–ei-kysymyksen tunnistaa vain sävelkulusta. Kysymyssanat alkavat sp- tai sk-, ja ne tulevat lauseen alkuun. <Hv l="sparos">Sparos</Hv> ja <Hv l="skoros">skoros</Hv> taipuvat, <Hv>spare</Hv> ja <Hv>skore</Hv> mukautuvat substantiiviinsa, ja <Hv>skorverdon</Hv> vaatii monikon genetiivin ja yksikössä olevan verbin. Suomalaiselle melkein kaikki tästä on tuttua omasta kielestä.

<Vocab />

<Check />
